import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";

import RenderIf from "common/components/RenderIf";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLeadListSources from "../../../datahooks/useLeadListSources";
import SourceItem from "./SourceItem";

import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from "react";

// Amount to scroll by when clicking the scroll buttons.
const SCROLL_AMOUNT_PX = 320;

// Guards against sub-pixel scroll position rounding differences across browsers.
const OVERFLOW_THRESHOLD_PX = 1;

// Minimum pointer movement (in px) before treating as a drag instead of a click.
const DRAG_THRESHOLD_PX = 15;

const SCROLL_BUTTON_CLASS_NAME =
  "shrink-0 w-5 my-auto h-18 flex items-center justify-center rounded-full bg-black-50 text-black-950 cursor-pointer transition-colors hover:bg-black-100 disabled:cursor-not-allowed disabled:text-black-400 disabled:hover:bg-black-50";

interface ScrollButtonProps {
  direction: "left" | "right";
  disabled: boolean;
  onClick: () => void;
}

function ScrollButton({ direction, disabled, onClick }: ScrollButtonProps) {
  const Icon = direction === "left" ? CaretLeft : CaretRight;

  return (
    <button
      type="button"
      aria-label={direction === "left" ? "Scroll left" : "Scroll right"}
      className={SCROLL_BUTTON_CLASS_NAME}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon className="size-4" />
    </button>
  );
}

interface LeadListSourcesProps {
  listId: string;
  selectedSourceId: string | null;
  selectSource: (sourceId: string) => void;
}

export default function LeadListSources({
  listId,
  selectedSourceId,
  selectSource,
}: LeadListSourcesProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { leadListSources, isLoadingLeadListSources } = useLeadListSources({
    listId,
  });
  const sources = leadListSources ?? [];

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const hasDraggedRef = useRef(false);

  const updateScrollButtonsState = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;

    setCanScrollLeft(scrollLeft > OVERFLOW_THRESHOLD_PX);
    setCanScrollRight(
      scrollWidth - (scrollLeft + clientWidth) > OVERFLOW_THRESHOLD_PX,
    );
  }, []);

  useEffect(() => {
    // Mobile relies on native touch scrolling, so there's no need to track
    // overflow state or render scroll buttons there.
    if (!isDesktop) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    updateScrollButtonsState();

    const resizeObserver = new ResizeObserver(updateScrollButtonsState);
    resizeObserver.observe(scrollContainer);

    return () => resizeObserver.disconnect();
  }, [isDesktop, updateScrollButtonsState, sources.length]);

  const scrollByAmount = (amount: number) => {
    scrollContainerRef.current?.scrollBy({
      left: amount,
      behavior: "smooth",
    });
  };

  // Lets mouse users click-and-drag to scroll horizontally, similar to a
  // carousel. Touch/pen input is left untouched so it keeps native scrolling.
  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartScrollLeftRef.current = e.currentTarget.scrollLeft;
    setIsDragging(true);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;

    const delta = e.clientX - dragStartXRef.current;

    if (!hasDraggedRef.current && Math.abs(delta) > DRAG_THRESHOLD_PX) {
      hasDraggedRef.current = true;
      // Capture pointer only after drag is confirmed to avoid interfering with normal clicks.
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    e.currentTarget.scrollLeft = dragStartScrollLeftRef.current - delta;
  };

  const stopDragging = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setIsDragging(false);
  };

  // Suppresses the click a drag gesture would otherwise trigger on the
  // source card it ends on top of (e.g. selecting it).
  const handleClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!hasDraggedRef.current) return;

    e.stopPropagation();
    e.preventDefault();
    hasDraggedRef.current = false;
  };

  if (isLoadingLeadListSources) {
    return (
      <div className="flex flex-row gap-2 overflow-hidden items-center p-1 -m-1">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
            key={index}
            className="w-[240px] shrink-0 p-4 bg-black-50 rounded-4xl flex flex-row items-center gap-3"
          >
            <Skeleton className="size-10 rounded-2xl" />

            <div className="flex flex-col gap-2 w-full">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const showScrollButtons = isDesktop && (canScrollLeft || canScrollRight);

  return (
    <div className="flex flex-row items-center gap-2 -m-1">
      <RenderIf condition={showScrollButtons}>
        <ScrollButton
          direction="left"
          disabled={!canScrollLeft}
          onClick={() => scrollByAmount(-SCROLL_AMOUNT_PX)}
        />
      </RenderIf>

      <div
        ref={scrollContainerRef}
        onScroll={isDesktop ? updateScrollButtonsState : undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onClickCapture={handleClickCapture}
        className={clsx(
          "flex overflow-x-auto flex-row gap-2 scrollbar-none p-1",
          // Disable smooth scroll while dragging for accurate cursor tracking.
          isDragging
            ? "scroll-auto cursor-grabbing select-none"
            : "scroll-smooth cursor-grab",
        )}
      >
        {sources.map((source) => {
          return (
            <SourceItem
              key={source.id}
              listId={listId}
              source={source}
              isSelected={source.id === selectedSourceId}
              selectSource={selectSource}
            />
          );
        })}
      </div>

      <RenderIf condition={showScrollButtons}>
        <ScrollButton
          direction="right"
          disabled={!canScrollRight}
          onClick={() => scrollByAmount(SCROLL_AMOUNT_PX)}
        />
      </RenderIf>
    </div>
  );
}
